import { useEffect, useRef, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, Target, Check, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const DRIVE = [
  ["light", "Light touch", "What he came for sits in the background. He follows wherever they take it and only steers when the talk stalls."],
  ["firm", "Firm, not an interviewer", "He leads, brings the archive, and pulls a drifting conversation back — but drops the agenda the moment someone has something real to say."],
  ["hard", "Hard drive", "He works through what he came for and does not let a sitting wander. Still an elder, never a form to be filled in."],
];

const Card = ({ title, note, right, children, testId }) => (
  <section className="min-w-0 break-words rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-4 sm:p-6" data-testid={testId}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="font-display text-base font-medium">{title}</h3>
        {note && <p className="mt-0.5 max-w-2xl text-xs text-[#6E7582]">{note}</p>}
      </div>
      {right}
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

const Area = ({ value, onChange, rows = 6, placeholder, testId }) => (
  <textarea
    data-testid={testId}
    value={value ?? ""}
    rows={rows}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="w-full resize-y rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 text-sm leading-relaxed text-[#F3EFE6] placeholder:text-[#4D5560] focus:border-[#E07A5F] focus:outline-none"
  />
);

export default function PurposePage() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const loaded = useRef(false);

  useEffect(() => {
    api.purpose().then((d) => { setP(d); loaded.current = true; }).catch((e) => toast.error(errMsg(e)));
  }, []);

  const objectives = p?.objectives || [];
  const set = (patch) => setP((cur) => ({ ...cur, ...patch }));
  const setObj = (i, patch) => set({ objectives: objectives.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  const move = (i, d) => {
    const next = [...objectives];
    const [row] = next.splice(i, 1);
    next.splice(i + d, 0, row);
    set({ objectives: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const d = await api.patchPurpose({
        active: p.active, mission: p.mission, objectives: p.objectives,
        outcome: p.outcome, close: p.close, drive: p.drive,
      });
      setP(d);
      setSavedAt(Date.now());
      toast.success("Purpose saved — the next conversation uses it");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Put back this Lab's default mission and objectives? Your edits will be lost.")) return;
    setSaving(true);
    try {
      setP(await api.resetPurpose());
      toast.success("Back to the default purpose");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!p) return <p className="text-sm text-[#6E7582]" data-testid="purpose-loading">Reading the purpose…</p>;

  return (
    <div className="space-y-6 pb-24" data-testid="purpose-page">
      <header>
        <p className="eyebrow">Studio</p>
        <h2 className="mt-1 font-display text-2xl font-semibold">Purpose</h2>
        <p className="mt-1.5 max-w-2xl text-sm text-[#A1A8B3]">
          Why anyone is talking to Griot, and what he is trying to come away with. This is read before his
          personality and it outranks it — the voice is only <em>how</em> he does this. Without it he has
          one move left: repeat your last word back as a question.
        </p>
      </header>

      <Card
        testId="purpose-active-card"
        title="Give him a job"
        note="Off, he reverts to an open-ended reflective listener with no agenda — companionable, and prone to circles."
        right={
          <Switch
            data-testid="purpose-active-toggle"
            checked={p.active !== false}
            onCheckedChange={(v) => set({ active: v })}
            className="data-[state=checked]:bg-[#E07A5F]"
          />
        }
      >
        <div className={p.active === false ? "pointer-events-none opacity-40" : ""}>
          <span className="text-xs text-[#6E7582]">The mission — one paragraph, written to him, in the second person</span>
          <div className="mt-1.5">
            <Area
              testId="purpose-mission"
              rows={8}
              value={p.mission}
              onChange={(v) => set({ mission: v })}
              placeholder="You are here to… — say what he is collecting, on whose behalf, and why it matters that he gets it."
            />
          </div>
        </div>
      </Card>

      <Card
        testId="purpose-objectives-card"
        title="What he is trying to come away with"
        note="Up to eight. He works them one at a time, in whatever order the conversation allows, and never reads them out — nobody should be able to tell a list exists."
        right={
          <button
            data-testid="purpose-add-objective-btn"
            onClick={() => set({ objectives: [...objectives, { text: "", got_it_when: "" }] })}
            disabled={objectives.length >= 8}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-[#D8D2C4] hover:border-[#E07A5F]/50 disabled:opacity-40"
          >
            <Plus size={13} /> Add
          </button>
        }
      >
        <ol className="space-y-3">
          {objectives.map((o, i) => (
            <li key={i} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4" data-testid={`purpose-objective-${i}`}>
              <div className="flex items-start gap-3">
                <span className="mt-2.5 font-mono text-[11px] text-[#E07A5F]">{String(i + 1).padStart(2, "0")}</span>
                <div className="min-w-0 flex-1 space-y-2">
                  <Area testId={`purpose-objective-text-${i}`} rows={2} value={o.text} onChange={(v) => setObj(i, { text: v })} placeholder="Find out…" />
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-[#6E7582]">He has it when</span>
                    <input
                      data-testid={`purpose-objective-when-${i}`}
                      value={o.got_it_when ?? ""}
                      onChange={(e) => setObj(i, { got_it_when: e.target.value })}
                      placeholder="they have named a specific thing, not a general wish"
                      className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-[#D8D2C4] placeholder:text-[#4D5560] focus:border-[#E07A5F] focus:outline-none"
                    />
                  </label>
                </div>
                <div className="flex shrink-0 flex-col gap-1">
                  <button data-testid={`purpose-objective-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} className="rounded-lg p-1.5 text-[#6E7582] hover:bg-white/[0.05] hover:text-[#F3EFE6] disabled:opacity-25"><ArrowUp size={13} /></button>
                  <button data-testid={`purpose-objective-down-${i}`} disabled={i === objectives.length - 1} onClick={() => move(i, 1)} className="rounded-lg p-1.5 text-[#6E7582] hover:bg-white/[0.05] hover:text-[#F3EFE6] disabled:opacity-25"><ArrowDown size={13} /></button>
                  <button data-testid={`purpose-objective-del-${i}`} onClick={() => set({ objectives: objectives.filter((_, j) => j !== i) })} className="rounded-lg p-1.5 text-[#6E7582] hover:bg-[#E07A5F]/15 hover:text-[#E07A5F]"><Trash2 size={13} /></button>
                </div>
              </div>
            </li>
          ))}
          {!objectives.length && (
            <li className="rounded-xl border border-dashed border-white/10 px-4 py-8 text-center text-xs text-[#6E7582]" data-testid="purpose-objectives-empty">
              No objectives. He will hold to the mission but have nothing specific to reach for.
            </li>
          )}
        </ol>
      </Card>

      <Card testId="purpose-drive-card" title="How hard he drives" note="The same mission, held at three different pressures.">
        <div className="grid gap-3 md:grid-cols-3">
          {DRIVE.map(([v, label, note]) => {
            const on = (p.drive || "firm") === v;
            return (
              <button
                key={v}
                data-testid={`purpose-drive-${v}`}
                onClick={() => set({ drive: v })}
                className={`rounded-xl border p-4 text-left transition-colors ${on ? "border-[#E07A5F] bg-[#E07A5F]/10" : "border-white/[0.07] bg-white/[0.02] hover:border-white/20"}`}
              >
                <span className="flex items-center gap-2 text-sm text-[#F3EFE6]">
                  {on ? <Check size={14} className="text-[#F4A261]" /> : <Target size={14} className="text-[#6E7582]" />} {label}
                </span>
                <span className="mt-1.5 block text-xs leading-relaxed text-[#6E7582]">{note}</span>
              </button>
            );
          })}
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card testId="purpose-outcome-card" title="What they should leave with" note="How the sitting should feel once it is over, from their side.">
          <Area testId="purpose-outcome" rows={5} value={p.outcome} onChange={(v) => set({ outcome: v })} placeholder="They should leave feeling…" />
        </Card>
        <Card testId="purpose-close-card" title="How he closes" note="A deliberate ending instead of a conversation that trails off: what he names back, and what he hands them to take away.">
          <Area testId="purpose-close" rows={5} value={p.close} onChange={(v) => set({ close: v })} placeholder="When you have what you came for…" />
        </Card>
      </div>

      <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] bg-[#121417]/95 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6 md:-mx-10 md:px-10">
        <button data-testid="purpose-reset-btn" onClick={reset} className="inline-flex items-center gap-1.5 text-xs text-[#6E7582] hover:text-[#F3EFE6]">
          <RotateCcw size={13} /> Back to the default purpose
        </button>
        <div className="flex items-center gap-4">
          {savedAt && <span className="text-xs text-[#6E7582]" data-testid="purpose-saved-note">Live in the next conversation</span>}
          <button
            data-testid="purpose-save-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-[#E07A5F] px-5 py-2.5 text-sm font-medium text-[#121417] transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save purpose
          </button>
        </div>
      </div>
    </div>
  );
}
